import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { NgxGanttModule, NgxGanttComponent, GanttItem, GanttGroup, GanttViewType } from '@worktile/gantt';
import { AccountUser } from '@models';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { DateFns } from '@utils';

export interface UserProgramTimelineDialogParams {
  accountUser: AccountUser;
}

interface ProgramAccessInfo {
  programId: string;
  programName: string;
  programUserGroupName: string;
  joinDate: Date;
  endDate: Date | null;
  isExcluded: boolean;
  isExpired: boolean;
  isFuture: boolean;
  programStatus: string;
}

@Component({
  selector: 'prox-user-program-timeline-dialog',
  standalone: true,
  imports: [CommonModule, ButtonModule, NgxGanttModule],
  template: `
    <div class="flex h-full flex-col">
      @if (isLoading) {
        <div class="flex h-full items-center justify-center">
          <i class="pi pi-spin pi-spinner text-2xl"></i>
        </div>
      } @else if (items.length === 0) {
        <div class="flex h-full items-center justify-center text-gray-500">
          <div class="text-center">
            <i class="pi pi-calendar mb-2 text-4xl"></i>
            <p>No program access history found for this user.</p>
          </div>
        </div>
      } @else {
        <div class="mb-2 flex items-center gap-2">
          <span class="mr-2 text-sm text-gray-600">View:</span>
          <button
            pButton
            [label]="'Month'"
            [class]="viewType === viewTypes.month ? 'p-button-sm' : 'p-button-sm p-button-outlined'"
            (click)="setViewType(viewTypes.month)"
          ></button>
          <button
            pButton
            [label]="'Quarter'"
            [class]="viewType === viewTypes.quarter ? 'p-button-sm' : 'p-button-sm p-button-outlined'"
            (click)="setViewType(viewTypes.quarter)"
          ></button>
          <button
            pButton
            [label]="'Year'"
            [class]="viewType === viewTypes.year ? 'p-button-sm' : 'p-button-sm p-button-outlined'"
            (click)="setViewType(viewTypes.year)"
          ></button>
        </div>
        <div class="min-h-0 flex-1">
          <ngx-gantt
            #gantt
            [items]="items"
            [groups]="groups"
            [viewType]="viewType"
            [start]="startDate"
            [end]="endDate"
            [showTodayLine]="true"
            [styles]="ganttStyles"
          >
            <ngx-gantt-table>
              <ngx-gantt-column name="Program" [width]="400">
                <ng-template #cell let-item="item">
                  <div class="flex items-center gap-2">
                    <span class="h-3 w-3 flex-shrink-0 rounded-full" [style.background-color]="item.color"></span>
                    <span class="whitespace-nowrap">{{ item.title }}</span>
                  </div>
                </ng-template>
              </ngx-gantt-column>
            </ngx-gantt-table>
            <ng-template #bar let-item="item">
              <div
                class="flex h-full items-center justify-center rounded px-2 text-xs text-white"
                [style.background-color]="item.color"
                [title]="getBarTooltip(item)"
              >
                {{ item.origin?.programUserGroupName || '' }}
              </div>
            </ng-template>
          </ngx-gantt>
        </div>
        <div class="mt-3 flex items-center gap-4 border-t pt-3 text-sm text-gray-600">
          <div class="flex items-center gap-2">
            <span class="h-3 w-3 rounded-full bg-green-500"></span>
            <span>Active</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="h-3 w-3 rounded-full bg-blue-500"></span>
            <span>Future</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="h-3 w-3 rounded-full bg-gray-400"></span>
            <span>Expired/Unavailable</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="h-3 w-3 rounded-full bg-red-400"></span>
            <span>Excluded</span>
          </div>
        </div>
      }
      <div class="mt-3 flex justify-start border-t pt-3">
        <button pButton label="Close" class="p-button-sm" (click)="onClose()"></button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }

      :host ::ng-deep .gantt-table-body {
        max-height: 400px;
        overflow-y: auto;
      }

      /* Make column resize handle more visible */
      :host ::ng-deep .column-resize-handle {
        width: 6px;
        right: -3px;
        background: transparent;
        transition: background 0.15s ease;
      }

      :host ::ng-deep .column-resize-handle:hover {
        background: #3b82f6;
      }

      /* Make table resize handle more visible */
      :host ::ng-deep .table-resize-handle {
        width: 6px;
        background: #e5e7eb;
        transition: background 0.15s ease;
      }

      :host ::ng-deep .table-resize-handle:hover {
        background: #3b82f6;
      }

      /* Style the auxiliary resize line */
      :host ::ng-deep .table-resize-auxiliary-line {
        background: #3b82f6;
      }
    `,
  ],
})
export class UserProgramTimelineDialogComponent implements OnInit {
  @ViewChild('gantt') ganttComponent!: NgxGanttComponent;

  isLoading = true;
  items: GanttItem[] = [];
  groups: GanttGroup[] = [];
  viewType = GanttViewType.year;
  startDate!: number;
  endDate!: number;

  // Expose view types to template
  viewTypes = GanttViewType;

  ganttStyles = {
    lineHeight: 44,
    barHeight: 24,
  };

  private accountUser!: AccountUser;
  private programAccessData: ProgramAccessInfo[] = [];

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private dbQueryService: AccountDbQueryService,
  ) {}

  async ngOnInit(): Promise<void> {
    const params = this.config.data as UserProgramTimelineDialogParams;
    this.accountUser = params.accountUser;
    await this.loadData();
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;

    try {
      // Get program access data for this user
      const accessData = await this.dbQueryService.getUserProgramAccessTimeline(this.accountUser.id);
      this.programAccessData = accessData;

      if (accessData.length === 0) {
        this.isLoading = false;
        return;
      }

      // Build gantt items
      this.buildGanttData();
    } catch (error) {
      console.error('Error loading program access timeline:', error);
    }

    this.isLoading = false;
  }

  private buildGanttData(): void {
    const today = new Date();
    let minDate = new Date();
    let maxDate = new Date();

    // Find date range
    for (const access of this.programAccessData) {
      if (access.joinDate < minDate) {
        minDate = access.joinDate;
      }
      const accessEnd = access.endDate || today;
      if (accessEnd > maxDate) {
        maxDate = accessEnd;
      }
    }

    // Add padding to date range (1 month before and after)
    minDate = new Date(minDate);
    minDate.setMonth(minDate.getMonth() - 1);
    maxDate = new Date(maxDate);
    maxDate.setMonth(maxDate.getMonth() + 2);

    this.startDate = minDate.getTime();
    this.endDate = maxDate.getTime();

    // Group by program
    const programMap = new Map<string, ProgramAccessInfo[]>();
    for (const access of this.programAccessData) {
      const existing = programMap.get(access.programId) || [];
      existing.push(access);
      programMap.set(access.programId, existing);
    }

    // Build items (one per program-usergroup combination)
    this.items = [];
    let itemIndex = 0;

    for (const [programId, accesses] of programMap) {
      for (const access of accesses) {
        const itemId = `item-${itemIndex++}`;
        const endDateValue = access.endDate || today;

        // Check if program is inactive (not Active status)
        const isProgramInactive = access.programStatus !== 'Active';

        // Determine color based on status
        let color = '#22c55e'; // green for active
        if (access.isExcluded) {
          color = '#f87171'; // red for excluded
        } else if (access.isExpired || isProgramInactive || (access.endDate && access.endDate < today)) {
          color = '#9ca3af'; // gray for expired/ended/unavailable
        } else if (access.isFuture) {
          color = '#3b82f6'; // blue for future
        }

        // Build display title with status
        let displayTitle = access.programName;
        if (access.isExcluded) {
          displayTitle += ' (Excluded)';
        } else if (access.isFuture) {
          displayTitle += ' (Future)';
        } else if (access.isExpired || (access.endDate && access.endDate < today)) {
          displayTitle += ' (Expired)';
        } else if (isProgramInactive) {
          displayTitle += ` (${access.programStatus})`;
        } else {
          displayTitle += ' (Active)';
        }

        this.items.push({
          id: itemId,
          title: displayTitle,
          start: access.joinDate.getTime(),
          end: endDateValue.getTime(),
          color,
          origin: access,
        });
      }
    }
  }

  getBarTooltip(item: GanttItem): string {
    const access = item.origin as ProgramAccessInfo;
    if (!access) return item.title;

    const startStr = DateFns.fmtDate(access.joinDate);
    const endStr = access.endDate ? DateFns.fmtDate(access.endDate) : 'Present';

    let tooltip = `${access.programName}\n`;
    tooltip += `User Group: ${access.programUserGroupName}\n`;
    tooltip += `Period: ${startStr} - ${endStr}`;

    if (access.isExcluded) {
      tooltip += '\n(Excluded from this program)';
    }

    return tooltip;
  }

  setViewType(type: GanttViewType): void {
    this.viewType = type;
  }

  onClose(): void {
    this.ref.close();
  }
}
